'use client'

import { useTranslations } from 'next-intl'

import { cn } from '@/utils'

export type ChipProps = {
  children?: React.ReactNode
  className?: string
  classNames?: { content?: string }
  handleClose?: () => void
  isCloseable?: boolean
  label?: string
  onClose?: () => void
  radius?: 'full' | 'lg' | 'md' | 'sm'
  variant?: 'bordered' | 'ghost' | 'solid'
  withCloseButton?: boolean
}

const radiusClasses: Record<NonNullable<ChipProps['radius']>, string> = {
  full: 'rounded-full',
  lg: 'rounded-lg',
  md: 'rounded-md',
  sm: 'rounded-sm'
}

export const Chip = ({
  children,
  className,
  classNames,
  handleClose,
  isCloseable,
  label,
  onClose,
  radius = 'full',
  variant = 'bordered',
  withCloseButton
}: ChipProps) => {
  const t = useTranslations('components.form.chip')
  const showClose = withCloseButton || isCloseable

  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 px-2.5 py-1 text-xs rtl:flex-row-reverse',
        radiusClasses[radius],
        variant === 'bordered' && 'border-input bg-background border',
        variant === 'solid' && 'bg-secondary text-secondary-foreground',
        variant === 'ghost' && 'bg-transparent',
        className
      )}
    >
      <span
        className={cn(
          'inline-flex min-w-0 items-center gap-1.5',
          classNames?.content
        )}
      >
        {children ?? label}
      </span>
      {showClose && (
        <button
          aria-label={t('removeAriaLabel')}
          className="bg-muted text-muted-foreground hover:bg-muted/80 ms-1 inline-flex size-4 items-center justify-center rounded-full"
          onClick={onClose ?? handleClose}
          type="button"
        >
          ×
        </button>
      )}
    </span>
  )
}
